import { useState, useEffect, useRef, useCallback, memo } from 'react'
import { useMessages } from '../../hooks/useMessages'
import useCRMStore from '../../store/useCRMStore'
import { contactAPI, messagesAPI, chatsAPI } from '../../services/api'
import { formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'
import { formatWhatsAppText } from '../../utils/whatsappFormatter'
import { AGENT_COLORS, getAgentAvatar } from '../../utils/constants'

// ========================================
// MediaContent - Renderiza media adjunta
// ========================================
const MediaContent = memo(function MediaContent({ msg, mediaUrls, onLoadMedia, isFromClient }) {
  const { messageType, mediaFileId, mediaMimetype } = msg
  if (!mediaFileId || messageType === 'chat') return null

  const mediaData = mediaUrls[mediaFileId]
  const colorClasses = isFromClient
    ? 'text-gray-500'
    : 'text-primary-200'

  // Solicitar media URL al montar (lazy)
  useEffect(() => {
    if (mediaFileId && !mediaData) {
      onLoadMedia(mediaFileId)
    }
  }, [mediaFileId, mediaData, onLoadMedia])

  // Estado: cargando
  if (!mediaData) {
    return (
      <div className="flex items-center gap-2 py-2">
        <div className="animate-pulse bg-gray-300 rounded w-48 h-32"></div>
      </div>
    )
  }

  // Error al cargar
  if (mediaData.error) {
    return (
      <div className={`flex items-center gap-2 py-1 text-xs ${colorClasses}`}>
        <span>📎 Archivo no disponible</span>
      </div>
    )
  }

  const mime = mediaData.mimetype || mediaMimetype || ''
  const type = messageType || (mime.startsWith('image') ? 'image' : mime.startsWith('video') ? 'video' : mime.startsWith('audio') ? 'audio' : 'document')

  // Imagen
  if (type === 'image' || mime.startsWith('image')) {
    return (
      <div className="py-1">
        <a href={mediaData.url} target="_blank" rel="noopener noreferrer">
          <img
            src={mediaData.preview || mediaData.url}
            alt="Imagen"
            className="rounded-lg max-w-xs max-h-64 object-cover cursor-pointer hover:opacity-90 transition"
            loading="lazy"
            onLoad={(e) => {
              // Si cargó el preview, reemplazar con la URL completa
              if (mediaData.preview && mediaData.url && e.target.src === mediaData.preview) {
                e.target.src = mediaData.url
              }
            }}
          />
        </a>
      </div>
    )
  }

  // Video
  if (type === 'video' || mime.startsWith('video')) {
    return (
      <div className="py-1">
        <video
          src={mediaData.url}
          controls
          className="rounded-lg max-w-xs max-h-64"
          preload="metadata"
        />
      </div>
    )
  }

  // Audio / PTT (voice note)
  if (type === 'audio' || type === 'ptt' || mime.startsWith('audio')) {
    return (
      <div className="py-1 flex items-center gap-2">
        <span className="text-lg">🎤</span>
        <audio src={mediaData.url} controls preload="metadata" className="max-w-xs h-8" />
      </div>
    )
  }

  // Documento
  if (type === 'document' || type === 'file') {
    const fileName = mediaData.name || 'Documento'
    const fileSize = mediaData.size ? `(${(mediaData.size / 1024).toFixed(0)} KB)` : ''
    return (
      <div className="py-1">
        <a
          href={mediaData.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
            isFromClient ? 'border-gray-200 bg-gray-50 hover:bg-gray-100' : 'border-primary-400 bg-primary-500 hover:bg-primary-400'
          } transition text-sm`}
        >
          <span className="text-lg">📄</span>
          <div className="min-w-0">
            <div className="truncate font-medium">{fileName}</div>
            {fileSize && <div className={`text-xs ${colorClasses}`}>{fileSize}</div>}
          </div>
        </a>
      </div>
    )
  }

  // Fallback genérico
  return (
    <div className={`flex items-center gap-2 py-1 text-xs ${colorClasses}`}>
      <span>📎 {mediaData.name || 'Archivo adjunto'}</span>
    </div>
  )
})



// ========================================
// ChatWindow - Componente principal
// ========================================
export default function ChatWindow({ selectedChat }) {
  const [messageText, setMessageText] = useState('')
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [mediaUrls, setMediaUrls] = useState({})
  const messagesEndRef = useRef(null)
  const messagesContainerRef = useRef(null)

  const { messages, loading, error, sending, sendMessage } = useMessages(selectedChat?.phone)

  const updateChat = useCRMStore(state => state.updateChat)

  // Marcar chat como leido al abrirlo
  useEffect(() => {
    if (selectedChat?.phone) {
      // Optimistic: limpiar badge local inmediatamente
      updateChat(selectedChat.phone, { unreadCount: 0 })
      // Persistir en servidor (fire & forget)
      chatsAPI.markAsRead(selectedChat.phone).catch(() => {})
    }
  }, [selectedChat?.phone])

  // Reset estado al cambiar de chat
  useEffect(() => {
    if (selectedChat?.phone) {
      setIsInitialLoad(true)
    }
  }, [selectedChat?.phone])

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messages.length > 0) {
      // Usar scroll instantáneo en la carga inicial, suave después
      const behavior = isInitialLoad ? 'instant' : 'smooth'
      messagesEndRef.current?.scrollIntoView({ behavior })

      // Marcar carga inicial como completada después del primer scroll
      if (isInitialLoad) {
        setIsInitialLoad(false)
      }
    }
  }, [messages, isInitialLoad])

  // Callback para cargar media URL de forma lazy
  const handleLoadMedia = useCallback(async (fileId) => {
    if (mediaUrls[fileId]) return // Ya cargado o cargando
    // Marcar como cargando (undefined -> loading state handled by component)
    try {
      const data = await messagesAPI.getMediaUrl(fileId)
      setMediaUrls(prev => ({ ...prev, [fileId]: data }))
    } catch (err) {
      console.error('Error cargando media:', err)
      setMediaUrls(prev => ({ ...prev, [fileId]: { error: true } }))
    }
  }, [mediaUrls])

  const handleSend = async (e) => {
    e.preventDefault()
    if (!messageText.trim() || sending) return

    const result = await sendMessage(messageText)
    if (result.success) {
      setMessageText('')
    }
  }

  if (!selectedChat) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-6xl mb-4">💬</p>
          <p className="text-lg font-medium text-gray-600">Selecciona un chat para comenzar</p>
          <p className="text-sm text-gray-500 mt-2">Haz click en cualquier conversación de la lista</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col bg-white">
      {/* Header del chat */}
      <div className="h-16 px-6 border-b border-gray-200 flex items-center justify-between bg-white shadow-sm">
        <div className="flex items-center gap-3">
          {(() => {
            const agent = getAgentAvatar(selectedChat.assignedTo)
            if (agent.initial) {
              return (
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-sm"
                  style={{ background: agent.gradient }}
                  title={selectedChat.assignedTo}
                >
                  {agent.initial}
                </div>
              )
            }
            return (
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center shadow-sm"
                style={{ background: agent.gradient }}
                title="Sin asignar"
              >
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
              </div>
            )
          })()}
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{selectedChat.name}</h2>
            <p className="text-sm text-gray-500">{selectedChat.phone}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {selectedChat.assignedTo && selectedChat.assignedTo !== 'Ninguno' && (
            <div className="px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-sm font-medium">
              👤 {selectedChat.assignedTo}
            </div>
          )}
          {selectedChat.stage && (
            <div className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm font-medium">
              {selectedChat.stage}
            </div>
          )}
        </div>
      </div>

      {/* Área de mensajes */}
      <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-500">Cargando mensajes...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-sm text-red-600 mb-2">Error al cargar mensajes</p>
              <p className="text-xs text-gray-500">{error}</p>
            </div>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto space-y-4">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex items-start gap-3 ${msg.isFromClient ? '' : 'justify-end'}`}
              >
                {msg.isFromClient && (
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 font-semibold text-xs flex-shrink-0">
                    {selectedChat.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                )}
                <div className={`flex-1 flex flex-col ${msg.isFromClient ? '' : 'items-end'}`}>
                  <div
                    className={`rounded-lg px-4 py-3 shadow-sm max-w-lg ${
                      msg.isFromClient
                        ? 'bg-white text-gray-900'
                        : 'bg-primary-600 text-white'
                    } ${msg.status === 'sending' ? 'animate-pulse opacity-70' : ''}`}
                  >
                    <MediaContent
                      msg={msg}
                      mediaUrls={mediaUrls}
                      onLoadMedia={handleLoadMedia}
                      isFromClient={msg.isFromClient}
                    />
                    {msg.text && (
                      <div className="text-sm whitespace-pre-wrap">{formatWhatsAppText(msg.text)}</div>
                    )}
                  </div>
                  <p className={`text-xs text-gray-500 mt-1 ${msg.isFromClient ? 'ml-2' : 'mr-2'}`}>
                    {formatDistanceToNow(new Date(msg.timestamp), { addSuffix: true, locale: es })}
                    {msg.status === 'sending' && ' (enviando...)'}
                    {msg.status === 'error' && ' (error al enviar)'}
                  </p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input de mensaje */}
      <form onSubmit={handleSend} className="border-t border-gray-200 p-4 bg-white">
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="p-2 text-gray-400 hover:text-gray-600 transition"
            disabled={sending}
          >
            <span className="text-xl">📎</span>
          </button>
          <input
            type="text"
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder="Escribe un mensaje..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
            disabled={sending}
          />
          <button
            type="submit"
            disabled={!messageText.trim() || sending}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? 'Enviando...' : 'Enviar'}
          </button>
        </div>
      </form>
    </div>
  )
}
